import { createContext, useContext, useState, useEffect } from "react";
import { gaEvent } from "../lib/ga";
import axios from "axios";
import { toast } from "sonner";
import { captureSignupAttribution, getSignupAttribution } from "../lib/signupAttribution";
import { wipeOffline, installOfflineGuards } from "../lib/offline";

captureSignupAttribution();

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const PENDING_ZIP_KEY = "af_pending_zip";
export const PENDING_AVAILABILITY_KEY = "af_pending_availability_optin";
export const SESSION_EXPIRED_MSG = "Please sign in again.";

const tokenExpired = (token) => {
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !!exp && exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

const redirectToLogin = () => {
  localStorage.removeItem("token");
  wipeOffline();
  const { pathname, search } = window.location;
  if (pathname.startsWith("/login")) return;
  window.location.assign(`/login?expired=1&redirect=${encodeURIComponent(pathname + search)}`);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      const detail = error?.response?.data?.detail;
      const sent = error?.config?.headers?.Authorization;
      const token = localStorage.getItem("token");
      if (error?.response?.status === 401 && ["Token expired", "Invalid token"].includes(detail)
          && token && sent === `Bearer ${token}`) {
        error.response.data.detail = SESSION_EXPIRED_MSG;
        redirectToLogin();
      }
      return Promise.reject(error);
    });
    const unguard = installOfflineGuards();
    const onWake = () => {
      const token = localStorage.getItem("token");
      if (token && document.visibilityState === "visible" && tokenExpired(token)) redirectToLogin();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      axios.interceptors.response.eject(id);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      unguard();
    };
  }, []);

  useEffect(() => {  // a different account signing in on this device never inherits offline copies
    const owner = localStorage.getItem("af-offline-owner");
    if (user?.id && owner && owner !== user.id) wipeOffline();
  }, [user?.id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedAdminToken = localStorage.getItem("adminToken");
    
    if (storedAdminToken) {
      setAdminToken(storedAdminToken);
    }
    
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  // A ZIP checked before signup/login is saved only to an account that has no ZIP yet (or the same ZIP).
  // A transient failure keeps the pending value for a retry; a rejected ZIP is dropped.
  useEffect(() => {
    const zip = sessionStorage.getItem(PENDING_ZIP_KEY);
    const token = localStorage.getItem("token");
    if (!user?.id || !zip || !token) return;
    const clearPending = () => { if (sessionStorage.getItem(PENDING_ZIP_KEY) === zip) { sessionStorage.removeItem(PENDING_ZIP_KEY); sessionStorage.removeItem(PENDING_AVAILABILITY_KEY); } };
    if (!/^\d{5}$/.test(zip)) { clearPending(); return; }
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/customer/preferences`, { headers })
      .then(({ data }) => {
        const saved = data?.preferences?.zip_code;
        if (saved && saved !== zip) { clearPending(); return null; }
        return axios.put(`${API}/customer/preferences`, { zip_code: zip, ...(sessionStorage.getItem(PENDING_AVAILABILITY_KEY) === "true" ? { notify_when_available: true, email_consent: true } : {}) }, { headers })
          .then(clearPending);
      })
      .catch((e) => {
        if (e?.response?.status === 422) clearPending();
        else toast.error("We couldn't save your ZIP yet. We'll try again shortly.");
      });
  }, [user?.id]);

  const fetchUser = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      if (error?.response) { localStorage.removeItem("token"); wipeOffline(); }  // offline (no response): keep the session for the offline page
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const signup = async (email, password, firstName, lastName, { zipCode = "", notify = false } = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      zip_code: zipCode || null,
      notify_when_available: notify,
      email_consent: notify,
      attribution: getSignupAttribution()
    });
    // The server already saved the ZIP and any opt-in from this form.
    sessionStorage.removeItem(PENDING_ZIP_KEY);
    sessionStorage.removeItem(PENDING_AVAILABILITY_KEY);
    localStorage.setItem("token", response.data.token);
    setUser(response.data.user);
    gaEvent("sign_up", { method: "email" });
    return response.data;
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    wipeOffline();
    setUser(null);
  };

  const adminLogin = async (username, password) => {
    const response = await axios.post(`${API}/admin/login`, { username, password });
    localStorage.setItem("adminToken", response.data.token);
    setAdminToken(response.data.token);
    return response.data;
  };

  const adminLogout = () => {
    localStorage.removeItem("adminToken");
    setAdminToken(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      await fetchUser(token);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      adminToken,
      loading,
      login,
      loginWithToken,
      signup,
      logout,
      adminLogin,
      adminLogout,
      refreshUser,
      isAuthenticated: !!user,
      isAdmin: !!adminToken
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
